import React from 'react';
import { useSelector } from 'react-redux';
import { Url } from 'src/api';
import CardContainer from 'src/components/common/CardContainer.redesign';
import LoadingPanel from 'src/components/common/LoadingPanel.redesign';
import Form from 'src/components/controls/forms/Form';
import FormTextBox from 'src/components/controls/forms/redesign/FormTextbox';
import { LandSystem } from 'src/models/LandSystem';
import SurfaceTract from 'src/models/SurfaceTract';
import { IRootState } from 'src/reducers';
import useDebounce from 'src/util/hooks/use-debounce';
import useFetch from 'use-http';
import SearchResultCard from '../tract/components/SearchResultCard';
import Tract from 'src/models/Tract';
import TractHorizonSummary from 'src/models/tractHorizonSummary';

interface ITractSearchProps {
    landSystem: LandSystem;
    tractType: 'Mineral' | 'Surface';
    onSurfaceTractSelected?: (surfaceTract: SurfaceTract) => void;
    onMineralTractSelected?: (mineralTract: Tract) => void;
}

const noop = (): void => {
    return null;
};

const TractSearch: React.FC<ITractSearchProps> = (props) => {
    const appContext = useSelector((state: IRootState) => state.appContext);
    const [loading, setLoading] = React.useState<boolean>(false);
    const { get, response } = useFetch();
    const [searchTerm, setSearchTerm] = React.useState<string>('');
    const [surfaceTracts, setSurfaceTracts] = React.useState<Array<SurfaceTract>>([]);
    const [mineralTracts, setMineralTracts] = React.useState<Array<Tract>>([]);
    const [selectedSurfaceTract, setSelectedSurfaceTract] = React.useState<SurfaceTract>(undefined);
    const [selectedMineralTract, setSelectedMineralTract] = React.useState<Tract>(undefined);

    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    const searchTracts = async () => {
        setLoading(true);
        setSelectedSurfaceTract(undefined);
        setSelectedMineralTract(undefined);

        const qs = new URLSearchParams({
            searchTerm: debouncedSearchTerm.toString(),
        });

        if (props.tractType === 'Surface') {
            let result = await get(`${Url.tract.getTract(appContext)}/surfaceTracts?${qs.toString()}`);
    
            if (result) {
                result = (result as Array<SurfaceTract>).map((s: SurfaceTract) => {
                    delete s.partialAcreage;
                    return s;
                });
            }
    
            if (response.ok) {
                setLoading(false);
                setSurfaceTracts(result);
            }
        } else if (props.tractType === 'Mineral') {
            let result = await get(`${Url.tract.getTracts(appContext, appContext.sectionNumber, '')}/surface-tracts/${appContext.surfaceTractNumber}/mineral-tracts?${qs.toString()}`);

            if (result) {
                result = (result as Array<TractHorizonSummary>).map((t: TractHorizonSummary) => {
                    t.horizons.forEach(h => {
                        delete h.partialAcreage;
                    });
                    return t;
                });
            }

            if (response.ok) {
                setLoading(false);
                setMineralTracts(result);
            }
        }
    };

    React.useEffect(() => {
        if (selectedSurfaceTract) {
            props.onSurfaceTractSelected(selectedSurfaceTract);
        }
    }, [selectedSurfaceTract]);

    React.useEffect(() => {
        if (selectedMineralTract) {
            props.onMineralTractSelected(selectedMineralTract);
        }
    });

    React.useEffect(() => {
        if (debouncedSearchTerm && debouncedSearchTerm.length >= 3) {
            searchTracts();
        }
    }, [debouncedSearchTerm]);

    const searchTermChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const updateSelectedTract = (surfaceTract: SurfaceTract) => {
        if (surfaceTract.id === selectedSurfaceTract?.id) {
            setSelectedSurfaceTract(undefined);
        } else {
            setSelectedSurfaceTract(surfaceTract);
        }
    };

    const tractPanel = () => {
        if (debouncedSearchTerm) {
            if (debouncedSearchTerm.length < 3) {
                return <div>Enter at least 3 characters to search</div>;
            }

            if (surfaceTracts && surfaceTracts.length > 0) {
                return (
                    <CardContainer>
                        {surfaceTracts.map((t: SurfaceTract) => (
                            <SearchResultCard key={t.id} item={t} isSelected={t.id === selectedSurfaceTract?.id} onSelected={updateSelectedTract}>
                                <span style={{ display: 'inline-block', width: '78%' }}>
                                    {props.landSystem === LandSystem.Eastern ? 'Parcel ' : ''}
                                    {t.name}
                                </span>
                            </SearchResultCard>
                        ))}
                    </CardContainer>
                );
            }

            if (mineralTracts && mineralTracts.length > 0) {
                return (
                    <CardContainer>
                        {mineralTracts.map((mt: Tract) => (
                            <SearchResultCard key={mt.id} item={mt} isSelected={mt.id === selectedSurfaceTract?.id} onSelected={updateSelectedTract}>
                                <span style={{ display: 'inline-block', width: '78%' }}>
                                    {props.landSystem === LandSystem.Eastern ? 'Parcel ' : ''}
                                    {mt.tractName}
                                </span>
                            </SearchResultCard>
                        ))}
                    </CardContainer>
                );
            }

            return <div>No Parcels matched search.</div>;
        }

        return <div>Enter Search Term above</div>;
    };

    const label =
        props.landSystem === LandSystem.Eastern ? `Search for Existing ${props.tractType} Parcel (only last 8 digits)` : `Search for Existing ${props.tractType} Tract`;

    return (
        <div>
            <Form onSubmit={noop}>
                <FormTextBox name="searchText" label={label} onChange={searchTermChange} />
            </Form>
            <div>
                <LoadingPanel isLoading={loading}>{tractPanel()}</LoadingPanel>
            </div>
        </div>
    );
};

export default TractSearch;



// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/TractSearch.tsx